import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '~/lib/api';
import {
  emptyDraft,
  errorMessage,
  hasErrors,
  submitKyb,
  validateAddress,
  validateBusiness,
  validateDirectors,
  validateDocuments,
  type Errors,
  type KybDraft,
} from '~/lib/kyb';
import { useSession } from '~/lib/session-context';
import { AddressStep } from '~/components/kyb/AddressStep';
import { BusinessStep } from '~/components/kyb/BusinessStep';
import { DirectorsStep } from '~/components/kyb/DirectorsStep';
import { DocumentsStep } from '~/components/kyb/DocumentsStep';
import { ReviewStep } from '~/components/kyb/ReviewStep';
import { Stepper, type StepDescriptor } from '~/components/kyb/Stepper';

type DashboardKybStatus = 'submitted' | 'in_review' | 'approved' | 'rejected';

interface KybCase {
  caseId: string;
  status: DashboardKybStatus;
  submittedAt: string;
  decidedAt: string | null;
  reason: string | null;
}

type View =
  | { kind: 'loading' }
  | { kind: 'form' }
  | { kind: 'case'; data: KybCase }
  | { kind: 'error'; message: string };

const STEPS: StepDescriptor[] = [
  { key: 'business', title: 'Business' },
  { key: 'address', title: 'Address' },
  { key: 'leadership', title: 'Leadership' },
  { key: 'documents', title: 'Documents' },
  { key: 'review', title: 'Review' },
];

/**
 * KYB landing + submission. `GET /dashboard/kyb` is the discriminator:
 *   404 → tenant hasn't submitted → render the stepper form
 *   200 → render the case readout
 *
 * The stepper is a wizard: each step validates before advancing, every
 * previously-completed step is reachable via the stepper itself (not just
 * Back), and the final Submit sends the whole thing. The actual document
 * bytes are already in Spaces by the time the user clicks Submit — the
 * stepper uploads them when they're picked in step 4.
 */
export function KybPage(): JSX.Element {
  const { me, refresh } = useSession();
  const [view, setView] = useState<View>({ kind: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await api<KybCase>('/dashboard/kyb');
      setView({ kind: 'case', data: res });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setView({ kind: 'form' });
      } else {
        setView({
          kind: 'error',
          message: err instanceof ApiError ? err.message : 'Could not load KYB case',
        });
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto w-full max-w-[880px] px-10 pt-8 pb-16">
      <h1 className="text-[22px] font-semibold -tracking-[0.015em]">
        Business verification
      </h1>
      <p className="mt-1 max-w-[64ch] text-[14.5px] text-ink-secondary">
        Seven needs to verify {me?.tenant.name ?? 'your business'} before you can
        issue live cards, mint live API keys, or receive production webhooks.
      </p>

      {view.kind === 'loading' && (
        <div className="mt-8 rounded-lg border border-rule-hairline bg-surface-slip p-6 text-[14.5px] text-ink-secondary">
          Loading…
        </div>
      )}

      {view.kind === 'error' && (
        <div className="mt-8 rounded-lg border border-stop/20 bg-stop-tint p-4 text-[14px] text-stop">
          {view.message}
        </div>
      )}

      {view.kind === 'case' && <CaseStatusCard data={view.data} />}

      {view.kind === 'form' && (
        <SubmissionWizard
          onSubmitted={async () => {
            // Refresh session so TopBar pill updates and the banner reconsiders.
            await refresh();
            await load();
          }}
        />
      )}
    </div>
  );
}

// ---------- the wizard ----------

function SubmissionWizard({
  onSubmitted,
}: {
  onSubmitted: () => Promise<void>;
}): JSX.Element {
  const [draft, setDraft] = useState<KybDraft>(emptyDraft);
  const [currentStep, setCurrentStep] = useState<string>('business');
  const [visitedErrors, setVisitedErrors] = useState<Record<string, Errors>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const stepErrors: Errors = useMemo(() => {
    switch (currentStep) {
      case 'business':
        return visitedErrors.business ?? {};
      case 'address':
        return visitedErrors.address ?? {};
      case 'leadership':
        return visitedErrors.leadership ?? {};
      case 'documents':
        return visitedErrors.documents ?? {};
      default:
        return {};
    }
  }, [currentStep, visitedErrors]);

  const completed: Set<string> = useMemo(() => {
    const set = new Set<string>();
    if (!hasErrors(validateBusiness(draft)) && visitedErrors.business !== undefined)
      set.add('business');
    if (!hasErrors(validateAddress(draft)) && visitedErrors.address !== undefined)
      set.add('address');
    if (
      !hasErrors(validateDirectors(draft)) &&
      visitedErrors.leadership !== undefined
    )
      set.add('leadership');
    if (
      !hasErrors(validateDocuments(draft)) &&
      visitedErrors.documents !== undefined
    )
      set.add('documents');
    return set;
  }, [draft, visitedErrors]);

  function update(patch: Partial<KybDraft>): void {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function validateCurrent(): Errors {
    switch (currentStep) {
      case 'business':
        return validateBusiness(draft);
      case 'address':
        return validateAddress(draft);
      case 'leadership':
        return validateDirectors(draft);
      case 'documents':
        return validateDocuments(draft);
      default:
        return {};
    }
  }

  function next(): void {
    const errors = validateCurrent();
    setVisitedErrors((prev) => ({ ...prev, [currentStep]: errors }));
    if (hasErrors(errors)) return;
    const idx = STEPS.findIndex((s) => s.key === currentStep);
    if (idx < STEPS.length - 1) {
      setCurrentStep(STEPS[idx + 1]!.key);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function back(): void {
    const idx = STEPS.findIndex((s) => s.key === currentStep);
    if (idx > 0) {
      setCurrentStep(STEPS[idx - 1]!.key);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function jumpTo(key: string): void {
    setCurrentStep(key);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    // Re-validate everything (in case the user bypassed via jump).
    const all: Record<string, Errors> = {
      business: validateBusiness(draft),
      address: validateAddress(draft),
      leadership: validateDirectors(draft),
      documents: validateDocuments(draft),
    };
    setVisitedErrors(all);
    const firstBad = Object.entries(all).find(([, e]) => hasErrors(e));
    if (firstBad) {
      setCurrentStep(firstBad[0]);
      setSubmitError('Fix the highlighted sections and submit again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    try {
      await submitKyb(draft);
      await onSubmitted();
    } catch (err) {
      setSubmitError(errorMessage(err, 'Submit failed. Try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  const isLast = currentStep === 'review';

  return (
    <div className="mt-8">
      <div className="mb-6 overflow-x-auto">
        <Stepper
          steps={STEPS}
          current={currentStep}
          completed={completed}
          onJump={jumpTo}
        />
      </div>

      {currentStep === 'business' && (
        <BusinessStep draft={draft} errors={stepErrors} update={update} />
      )}
      {currentStep === 'address' && (
        <AddressStep draft={draft} errors={stepErrors} update={update} />
      )}
      {currentStep === 'leadership' && (
        <DirectorsStep draft={draft} errors={stepErrors} update={update} />
      )}
      {currentStep === 'documents' && (
        <DocumentsStep draft={draft} errors={stepErrors} update={update} />
      )}
      {currentStep === 'review' && <ReviewStep draft={draft} />}

      {submitError && (
        <div
          role="alert"
          className="mt-4 rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] leading-[18px] text-stop"
        >
          {submitError}
        </div>
      )}

      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={back}
          disabled={currentStep === 'business'}
          className="h-9 rounded-md px-3 text-[14px] font-medium text-ink-secondary hover:bg-surface-margin hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
        >
          Back
        </button>
        {isLast ? (
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={submitting}
            className="inline-flex h-9 items-center justify-center rounded-md bg-ink-primary px-4 text-[14px] font-semibold text-ink-on-dark hover:bg-ink-primary/90 disabled:opacity-60"
          >
            {submitting ? 'Submitting…' : 'Submit for review'}
          </button>
        ) : (
          <button
            type="button"
            onClick={next}
            className="inline-flex h-9 items-center justify-center rounded-md bg-ink-primary px-4 text-[14px] font-semibold text-ink-on-dark hover:bg-ink-primary/90"
          >
            Continue
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- post-submit view (unchanged in intent from the earlier PR) ----------

function CaseStatusCard({ data }: { data: KybCase }): JSX.Element {
  const copy = STATUS_COPY[data.status];
  return (
    <section className="mt-8 rounded-lg border border-rule-hairline bg-surface-slip p-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div
            className={`font-mono inline-flex items-center rounded-full px-2 py-[3px] text-[10.5px] font-semibold uppercase tracking-[0.08em] ${copy.tone}`}
          >
            {copy.label}
          </div>
          <h2 className="mt-3 text-[18px] font-semibold text-ink-primary">
            {copy.title}
          </h2>
          <p className="mt-1 max-w-[52ch] text-[14.5px] leading-[20px] text-ink-secondary">
            {copy.body}
          </p>
          {data.reason && (
            <p className="mt-3 rounded border border-rule-hairline bg-surface-margin px-3 py-2 text-[13.5px] text-ink-primary">
              Reviewer note: {data.reason}
            </p>
          )}
        </div>
        <dl className="shrink-0 space-y-2 text-right text-[13px]">
          <div>
            <dt className="uppercase tracking-[0.08em] text-ink-watermark">
              Case
            </dt>
            <dd className="font-mono mt-0.5 text-ink-primary">{data.caseId}</dd>
          </div>
          <div>
            <dt className="uppercase tracking-[0.08em] text-ink-watermark">
              Submitted
            </dt>
            <dd className="font-mono mt-0.5 text-ink-primary">
              {formatDate(data.submittedAt)}
            </dd>
          </div>
          {data.decidedAt && (
            <div>
              <dt className="uppercase tracking-[0.08em] text-ink-watermark">
                Decided
              </dt>
              <dd className="font-mono mt-0.5 text-ink-primary">
                {formatDate(data.decidedAt)}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </section>
  );
}

const STATUS_COPY: Record<
  DashboardKybStatus,
  { label: string; tone: string; title: string; body: string }
> = {
  submitted: {
    label: 'Submitted',
    tone: 'bg-notice-tint text-notice',
    title: 'Your documents are with our reviewers',
    body: 'We handle most submissions within one business day. You can keep exploring the dashboard on sandbox data in the meantime.',
  },
  in_review: {
    label: 'In review',
    tone: 'bg-footnote-tint text-footnote',
    title: 'A reviewer is looking at your file',
    body: 'If we need anything else, we will email the owner on file.',
  },
  approved: {
    label: 'Approved',
    tone: 'bg-tally-tint text-tally',
    title: "You're verified",
    body: 'Live cards, API keys, and webhooks are open. If this page is still showing, refresh — the dashboard chrome catches up on the next load.',
  },
  rejected: {
    label: 'Rejected',
    tone: 'bg-stop-tint text-stop',
    title: "We couldn't approve this submission",
    body: 'See the reviewer note below. Resubmit by starting a new case — the owner on file can reach out when you are ready.',
  },
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
